import React from 'react';
import PropTypes from 'prop-types';
import { PageWrapper, Paper, FlexTableRow } from 'virtues-bootstrap';
import { map } from 'ramda';
import { withTranslation } from 'react-i18next';

import { UserRoleType } from 'types';
import { mapIndex } from 'utilities/ramda';

import { PATIENT, SUPER_RESEARCHER, SUPER_TECHNICIAN } from 'constants/userRoles';

import PaginationSection from 'components/ui/PaginationSection';
import PaperWithLoader from 'components/ui/PaperWithLoader';
import HeaderColumn from 'components/ui/HeaderColumn';

import usePagination from 'hook/usePagination';
import { requestObservationHistory } from 'services/api/request/reports';
import { handleReportsHistoryData } from 'store/selectors/reports/recent';
import PageHeader from 'containers/ReportsHistory/PageHeader';

import { getRowComponentByRole, getTableHeaderByRole } from './utils';

const ReportsHistory = ({ isDoctor, patientId, role, reportBy, t, performer }) => {
  const isSuperRole = [SUPER_RESEARCHER, SUPER_TECHNICIAN].includes(role);
  const status = role === PATIENT ? 'final,preliminary' : null;

  const [{ data, initial, fetching, pagination, error, sorting }, getData] = usePagination({
    request: requestObservationHistory,
    params: {
      status,
      count: 10,
      patientId,
      sort: 'date',
      desc: true,
      ...(!isSuperRole && performer && { performer }),
    },
    selector: handleReportsHistoryData,
  });

  const { total, page, count } = pagination;
  const { sort, desc } = sorting;

  const handlePageChange = selectedPage => {
    getData({
      pagesOffset: count * selectedPage,
      page: selectedPage,
    });
  };

  const headerColumns = getTableHeaderByRole(role);
  const RowComponent = getRowComponentByRole(role);

  return (
    <PageWrapper>
      <PageHeader role={role} patientId={patientId} />

      <PaperWithLoader error={error} loading={initial || fetching}>
        <Paper>
          <FlexTableRow heading>
            {map(
              ({ title, sortKey }) => (
                <HeaderColumn
                  key={title}
                  sort={sort}
                  currentKey={sortKey}
                  title={t(title)}
                  desc={desc}
                  handleClick={getData}
                />
              ),
              headerColumns,
            )}
          </FlexTableRow>
          {mapIndex(
            (item, index) => (
              <RowComponent
                key={index}
                {...item}
                reportBy={reportBy}
                patientId={patientId}
                isDoctor={isDoctor}
              />
            ),
            data,
          )}
        </Paper>
        <PaginationSection
          activePage={page}
          disableInitialCallback
          onPageChange={handlePageChange}
          itemsPerPage={count}
          textKey="pagination.reports"
          total={total}
        />
      </PaperWithLoader>
    </PageWrapper>
  );
};

ReportsHistory.propTypes = {
  role: UserRoleType.isRequired,
  patientId: PropTypes.string.isRequired,
  isDoctor: PropTypes.bool,
  t: PropTypes.func.isRequired,
  reportBy: PropTypes.string,
  performer: PropTypes.string,
};

ReportsHistory.defaultProps = {
  reportBy: null,
  performer: null,
  isDoctor: false,
};

export default withTranslation()(ReportsHistory);
